"use client";

import { ChevronRight, Music2, Pause, Play } from "lucide-react";
import { motion, useTransform } from "motion/react";
import { useState } from "react";
import type { MusicAttachment } from "@/lib/music/attachment";
import { musicArtworkSrc } from "@/lib/music/links";
import { SONG_CARD_HEIGHT } from "@/lib/music/song-card-metrics";
import { cn } from "@/lib/utils";
import { PlayingBars } from "./playing-bars";
import { SongSheet } from "./song-sheet";
import { type PreviewTarget, useMusicPreview } from "./use-music-preview";

/**
 * A playable song card: a chat "song" message, or a post that is just a song.
 * Fixed height (`SONG_CARD_HEIGHT`) so chat row estimates and feed layout
 * never wait on it. The play button previews in place; the rest of the card
 * opens the song sheet (listen on…, send, post with it, add to picks).
 */
export function SongCard({
  music,
  surface,
  className,
  interactive = true,
  onOpen,
  sheetLayer,
}: {
  music: MusicAttachment;
  surface: PreviewTarget["surface"];
  className?: string;
  /** False for static copies (chat's long-press overlay): no audio, no sheet. */
  interactive?: boolean;
  /**
   * Replaces the song sheet on a tap of the card body — a composer opens its
   * song picker instead (the sheet's "post with it" makes no sense there).
   */
  onOpen?: () => void;
  /**
   * Set when the card itself sits inside a drawer (a comments sheet): the song
   * sheet opens nested, at this layer, instead of under it.
   */
  sheetLayer?: number;
}) {
  const preview = useMusicPreview();
  const [sheetOpen, setSheetOpen] = useState(false);
  const playing =
    preview.key === music.songId &&
    (preview.status === "playing" || preview.status === "loading");
  const progressWidth = useTransform(preview.progress, (p) => `${p * 100}%`);
  const art = musicArtworkSrc(music.artworkUrl, 144);
  const tint = music.artworkBgColor ? `#${music.artworkBgColor}` : "#3b2f63";

  return (
    <div
      className={cn(
        "relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-border bg-surface-alt p-2 dark:border-glass-border-10",
        className,
      )}
      style={{
        height: SONG_CARD_HEIGHT,
        backgroundImage: `linear-gradient(110deg, color-mix(in oklab, ${tint} 38%, transparent) 0%, transparent 75%)`,
      }}
    >
      <button
        type="button"
        disabled={!interactive || !music.previewUrl}
        onClick={(event) => {
          event.stopPropagation();
          preview.toggle({
            key: music.songId,
            previewUrl: music.previewUrl,
            surface,
          });
        }}
        aria-label={playing ? "Pause preview" : `Play ${music.title}`}
        className="relative size-[72px] shrink-0 overflow-hidden rounded-xl bg-surface shadow-sm active:scale-95"
      >
        {art ? (
          // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
          <img
            src={art}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <Music2 className="m-auto size-6 text-text-tertiary" />
        )}
        {music.previewUrl ? (
          <span className="absolute inset-0 flex items-center justify-center bg-black/25">
            <span className="flex size-8 items-center justify-center rounded-full bg-white/95 text-neutral-900 shadow-md">
              {playing ? (
                <Pause className="size-4" fill="currentColor" strokeWidth={0} />
              ) : (
                <Play
                  className="ml-0.5 size-4"
                  fill="currentColor"
                  strokeWidth={0}
                />
              )}
            </span>
          </span>
        ) : null}
      </button>
      <button
        type="button"
        disabled={!interactive}
        onClick={(event) => {
          event.stopPropagation();
          if (onOpen) onOpen();
          else setSheetOpen(true);
        }}
        className="flex h-full min-w-0 flex-1 items-center gap-2 text-left"
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-1 text-[11px] font-semibold tracking-wide text-brand uppercase">
            {playing ? (
              <PlayingBars className="h-2.5" barClassName="w-[2px]" />
            ) : (
              <Music2 className="size-3" />
            )}
            Song
          </span>
          <span className="truncate text-sm leading-5 font-semibold text-text-primary">
            {music.title}
          </span>
          <span className="truncate text-xs leading-4 text-text-secondary">
            {music.artistName}
          </span>
        </span>
        {interactive ? (
          <ChevronRight className="size-4 shrink-0 text-text-tertiary" />
        ) : null}
      </button>
      {playing ? (
        <motion.span
          aria-hidden
          className="absolute bottom-0 left-0 h-0.5 bg-brand"
          style={{ width: progressWidth }}
        />
      ) : null}
      {interactive && !onOpen ? (
        <SongSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          music={music}
          nested={sheetLayer !== undefined}
          layer={sheetLayer}
        />
      ) : null}
    </div>
  );
}
